// src/pages/UserSettings/VulnAlerts/VulnAlerts.tab.tsx
import {
  Box,
  Button,
  Card,
  Divider,
  Flex,
  MultiSelect,
  Slider,
  Switch,
  Text,
  Title,
} from '@mantine/core';
import { isNotEmpty, useForm } from '@mantine/form';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFetchUserHasPermissions } from '../../../api/user/useFetchUserHasPermissions';
import Icon from '../../../components/Icon';
import Loading from '../../../components/Loading';
import { ManifestCheckbox } from '../../../components/ManifestCheckbox/ManifestCheckbox';
import { useNotifications } from '../../../hooks/utils/useNotifications';
import { useVulnerabilityAlertConfig } from './useVulnerabilityAlertConfig.hook';
import styles from './VulnAlerts.module.scss';
import axios from 'axios';

/* ============================================================================
 * Types & Constants
 * ========================================================================== */
interface VulnerabilityAlertConfiguration {
  isActive: boolean;
  alertOnKev: boolean;
  alertOnRecommendation: string[];
  alertOnSeverity: number;
  alertOnExploitability: number; // 0–1 (two‑decimal precision)
  targetEmails: string[];
}

/** Helper – mitigate floating‑point precision noise. */
const roundToTwo = (num: number) => Math.round((num + Number.EPSILON) * 100) / 100;
const RECOMMENDATION_KEYS = ['mitigate', 'monitor', 'accept'] as const;

/* ============================================================================
 * Component
 * ========================================================================== */
export const VulnAlertsTab: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { fetchConfig, saveConfig } = useVulnerabilityAlertConfig();
  const { success: notifySuccess, error: notifyError } = useNotifications();

  /* ------------------------------------------------------------------------
   * Local UI State (kept outside the form on purpose)
   * ---------------------------------------------------------------------- */
  const [recEnabled, setRecEnabled] = useState(false);
  const [severityEnabled, setSeverityEnabled] = useState(false);
  const [exploitEnabled, setExploitEnabled] = useState(false);
  const [emailOptions, setEmailOptions] = useState<string[]>([]);
  const [inited, setInited] = useState(false);
  const [prevSeverity, setPrevSeverity] = useState<number>(8);
  const [prevExploit, setPrevExploit] = useState<number>(0.8);
  const [emailSearch, setEmailSearch] = useState<string>(''); // controlled search text
  const [criteriaError, setCriteriaError] = useState(false);

  /* ------------------------------------------------------------------------
   * Form ‑ main data model sent to the API
   * ---------------------------------------------------------------------- */
  const form = useForm<VulnerabilityAlertConfiguration>({
    initialValues: {
      isActive: false,
      alertOnKev: false,
      alertOnRecommendation: [],
      alertOnSeverity: 0,
      alertOnExploitability: 0,
      targetEmails: [],
    },
    validate: {
      targetEmails: isNotEmpty(
        <Flex direction="row" align="center" gap={4}>
          <Icon classNames={styles.error} icon="triangle-exclamation" />
          <span>{t('page.userSettings.vulnAlerts.validation.addAtLeastOneEmail')}</span>
        </Flex>,
      ),
    },
  });

  /* ------------------------------------------------------------------------
   * Helpers
   * ---------------------------------------------------------------------- */
  const canUpdateVulnAlertSettings = useFetchUserHasPermissions({
    requiredPermissions: ['create:vulnerability-alert-configuration'],
  });

  const showGenericError = () =>
    notifyError({
      title: t('page.userSettings.vulnAlerts.validation.requestError'),
      autoClose: 5000,
    });

  /* ------------------------------------------------------------------------
   * Misc Helpers
   * ---------------------------------------------------------------------- */
  /** Adds a new email to both the selectable list and the current selection. */
  const handleCreateEmail = (query: string) => {
    const email = query.trim();
    if (!email) return email;

    if (!emailOptions.includes(email)) setEmailOptions((prev) => [...prev, email]);
    if (!form.values.targetEmails.includes(email)) {
      form.setFieldValue('targetEmails', [...form.values.targetEmails, email]);
    }
    return email;
  };

  /** Human‑readable label summarising selected recommendations. */
  const recommendationLabel = useMemo(() => {
    const recs = form.values.alertOnRecommendation;
    if (recs.length === 0) {
      return t('page.userSettings.vulnAlerts.recommendation.title', {
        recommendation: '',
      });
    }
    const labels = recs.map((key) =>
      t(`page.userSettings.vulnAlerts.recommendation.${key}`),
    );
    return t('page.userSettings.vulnAlerts.recommendation.title', {
      recommendation: labels.join(', '),
    }).trimEnd();
  }, [form.values.alertOnRecommendation, t, i18n.language]);

  /* ------------------------------------------------------------------------
   * Save handler ‑ aborts if form or custom validations fail
   * ---------------------------------------------------------------------- */
  const handleSave = () => {
    setCriteriaError(false);
    const { hasErrors } = form.validate(); // built‑in rules

    // Custom validation – at least one recommendation if toggle is on
    let customErrors = false;
    if (recEnabled && form.values.alertOnRecommendation.length === 0) {
      form.setFieldError(
        'alertOnRecommendation',
        t('page.userSettings.vulnAlerts.validation.selectAtLeastOneOption'),
      );
      customErrors = true;
    }

    const noCriteria =
      !form.values.alertOnKev &&
      !(recEnabled && form.values.alertOnRecommendation.length > 0) &&
      !severityEnabled &&
      !exploitEnabled;

    if (noCriteria) {
      setCriteriaError(true);
      return;
    }
    if (hasErrors || customErrors) return;

    // Prepare payload, sending 0 for disabled slider thresholds
    const payload = {
      ...form.values,
      alertOnSeverity: severityEnabled ? form.values.alertOnSeverity : 0,
      alertOnExploitability: exploitEnabled ? form.values.alertOnExploitability : 0,
    };

    saveConfig.mutate(payload, {
      /* ------------------------ HTTP OK ----------------------- */
      onSuccess: (res) => {
        if (res.success) {
          notifySuccess({
            title: t('page.userSettings.vulnAlerts.changesSaved'),
            message: t('page.userSettings.vulnAlerts.savedSuccessMessage'),
            autoClose: 4000,
          });
        } else {
          // Controlled failure (success: false)
          showGenericError();
        }
      },
      /* ------------------------ HTTP error -------------------- */
      onError: () => showGenericError(),
    });
  };

  /* ------------------------------------------------------------------------
   * Handle HTTP/network errors coming from the query
   * ---------------------------------------------------------------------- */
  useEffect(() => {
    if (fetchConfig.isError) showGenericError();
  }, [fetchConfig.isError]);

  /* ------------------------------------------------------------------------
   * Effect ‑ hydrate initial configuration (only if success flag is true)
   * ---------------------------------------------------------------------- */
  useEffect(() => {
    if (
      fetchConfig.isSuccess &&
      !inited &&
      fetchConfig.data?.data &&
      fetchConfig.data?.success
    ) {
      const d = fetchConfig.data.data;

      const initialSeverity = d.alertOnSeverity > 0 ? d.alertOnSeverity : 8;
      const rawExploit = roundToTwo(d.alertOnExploitability);
      const initialExploit = rawExploit > 0 ? rawExploit : 0.8;

      form.setValues({
        isActive: d.isActive,
        alertOnKev: d.alertOnKev,
        alertOnRecommendation: Array.isArray(d.alertOnRecommendation)
          ? d.alertOnRecommendation
          : [],
        alertOnSeverity: initialSeverity,
        alertOnExploitability: initialExploit,
        targetEmails: d.targetEmails,
      });

      setPrevSeverity(initialSeverity);
      setPrevExploit(initialExploit);
      setRecEnabled(
        Array.isArray(d.alertOnRecommendation) && d.alertOnRecommendation.length > 0,
      );
      setSeverityEnabled(d.alertOnSeverity > 0);
      setExploitEnabled(d.alertOnExploitability > 0);
      setEmailOptions(d.targetEmails);
      setInited(true);
    }

    // Controlled failure: success === false
    if (fetchConfig.isSuccess && fetchConfig.data && !fetchConfig.data.success) {
      showGenericError();
    }
  }, [fetchConfig.isSuccess, fetchConfig.data, inited, form]);

  useEffect(() => {
    if (fetchConfig.isError) {
      const err = fetchConfig.error;
      if (!axios.isAxiosError(err) || err.response?.status !== 404) {
        showGenericError();
      }
    }
  }, [fetchConfig.isError, fetchConfig.error]);

  /* ------------------------------------------------------------------------
   * Render guards
   * ---------------------------------------------------------------------- */
  if (fetchConfig.isLoading) return <Loading />;

  const isRealError =
    fetchConfig.isError &&
    (!axios.isAxiosError(fetchConfig.error) ||
      fetchConfig.error.response?.status !== 404);

  const isControlledFailure =
    fetchConfig.isSuccess && fetchConfig.data && !fetchConfig.data.success;

  if (isRealError || isControlledFailure) {
    return (
      <Flex justify="center" className={styles.fetchFailedContainer}>
        <Card w="50%" className={styles.fetchFailed}>
          <Text>{t('page.userSettings.vulnAlerts.validation.fetchFailed')}</Text>
        </Card>
      </Flex>
    );
  }

  /* ------------------------------------------------------------------------
   * UI
   * ---------------------------------------------------------------------- */
  return (
    <div className="anim-slideInDownShort">
      <Flex maw={650} w="100%" mx="auto" direction="column">
        {/* ---------- Header ---------- */}
        <Flex justify="space-between" align="center" mb={20}>
          <Title order={2}>{t('page.userSettings.vulnAlerts.title')}</Title>
          <Button
            onClick={handleSave}
            loading={saveConfig.isLoading}
            disabled={saveConfig.isLoading || !canUpdateVulnAlertSettings}
          >
            {t('global.save.buttonShort')}
          </Button>
        </Flex>
        <Divider mb={32} />

        {/* ---------- Daily alerts toggle ---------- */}
        <Flex justify="space-between" mb={10}>
          <Text fw={700}>{t('page.userSettings.vulnAlerts.dailyAlerts')}</Text>
          <Switch {...form.getInputProps('isActive', { type: 'checkbox' })} />
        </Flex>
        <Text className={styles.description} mb={24}>
          {t('page.userSettings.vulnAlerts.dailyAlertsDescription')}
        </Text>

        {form.values.isActive && (
          <>
            {/* ================================================================
             * CRITERIA (Kev, Recommendation, Severity, Exploitability)
             * ============================================================== */}
            <Card p={24} mb={16}>
              <Flex align="center" gap={4} mb={4}>
                <Icon icon="sliders" className={styles.slidersIcon} />
                <Text fw={700}>
                  {t('page.userSettings.vulnAlerts.criteria')}
                  <span className={styles.requiredStar}>*</span>
                </Text>
                {criteriaError && (
                  <Flex
                    direction="row"
                    align="center"
                    gap={4}
                    className={styles.noCriteriaSelectedErrorContainer}
                  >
                    <Icon classNames={styles.error} icon="triangle-exclamation" />
                    <Text color="red">
                      {t(
                        'page.userSettings.vulnAlerts.validation.selectAtLeastOneOption',
                      )}
                    </Text>
                  </Flex>
                )}
              </Flex>

              <Box ml="xl">
                <Text className={styles.description} mb={24}>
                  {t('page.userSettings.vulnAlerts.criteriaDescription')}
                </Text>

                {/* --- CISA KEV ------------------------------------------------ */}
                <ManifestCheckbox
                  label={t('page.userSettings.vulnAlerts.onKevList')}
                  {...form.getInputProps('alertOnKev', { type: 'checkbox' })}
                  mb={24}
                />

                {/* --- Recommendation toggle & checkboxes -------------------- */}
                <Flex direction="column">
                  <Flex direction="row" gap={10} mb={recEnabled ? 0 : 24}>
                    <ManifestCheckbox
                      label={
                        <Flex direction="row" gap={4}>
                          {recommendationLabel}{' '}
                          {recEnabled && form.errors.alertOnRecommendation && (
                            <Flex direction="row" align="center" gap={4}>
                              <Icon
                                classNames={styles.error}
                                icon="triangle-exclamation"
                              />
                              <Text color="red" size="sm">
                                {form.errors.alertOnRecommendation}
                              </Text>
                            </Flex>
                          )}
                        </Flex>
                      }
                      checked={recEnabled}
                      onChange={(e) => {
                        const on = e.currentTarget.checked;
                        setRecEnabled(on);
                        if (!on) form.setFieldValue('alertOnRecommendation', []);
                      }}
                      mb={recEnabled ? 12 : 0}
                    />
                  </Flex>

                  {recEnabled && (
                    <Flex direction="column" ml="xl" mb={20}>
                      {RECOMMENDATION_KEYS.map((key) => (
                        <ManifestCheckbox
                          key={key}
                          label={t(`page.userSettings.vulnAlerts.recommendation.${key}`)}
                          checked={form.values.alertOnRecommendation.includes(key)}
                          onChange={(e) => {
                            const checked = e.currentTarget.checked;
                            const arr = form.values.alertOnRecommendation;
                            form.setFieldValue(
                              'alertOnRecommendation',
                              checked ? [...arr, key] : arr.filter((k) => k !== key),
                            );
                          }}
                          mb={12}
                        />
                      ))}
                    </Flex>
                  )}
                </Flex>

                {/* --- Severity threshold ------------------------------------ */}
                <Flex direction="column" mb={24}>
                  <ManifestCheckbox
                    label={t('page.userSettings.vulnAlerts.severityThreshold', {
                      severity: severityEnabled
                        ? `${form.values.alertOnSeverity} ` +
                          t('page.userSettings.vulnAlerts.orHigher')
                        : '',
                    })}
                    checked={severityEnabled}
                    onChange={(e) => {
                      const on = e.currentTarget.checked;
                      setSeverityEnabled(on);
                      if (!on) {
                        setPrevSeverity(form.values.alertOnSeverity);
                        form.setFieldValue('alertOnSeverity', 0);
                      } else {
                        form.setFieldValue('alertOnSeverity', prevSeverity);
                      }
                    }}
                    mb={severityEnabled ? 12 : 0}
                  />
                  {severityEnabled && (
                    <Slider
                      min={0.1}
                      max={10}
                      step={0.1}
                      value={form.values.alertOnSeverity}
                      onChange={(val) => {
                        form.setFieldValue('alertOnSeverity', val);
                        setPrevSeverity(val);
                      }}
                      ml="xl"
                      inverted
                    />
                  )}
                </Flex>

                {/* --- Exploitability threshold ------------------------------ */}
                <Flex direction="column" mb={24}>
                  <ManifestCheckbox
                    label={t('page.userSettings.vulnAlerts.exploitThreshold', {
                      exploit: exploitEnabled
                        ? `${Math.round(form.values.alertOnExploitability * 100)}% ` +
                          t('page.userSettings.vulnAlerts.orHigher')
                        : '',
                    })}
                    checked={exploitEnabled}
                    onChange={(e) => {
                      const on = e.currentTarget.checked;
                      setExploitEnabled(on);
                      if (!on) {
                        setPrevExploit(form.values.alertOnExploitability);
                        form.setFieldValue('alertOnExploitability', 0);
                      } else {
                        form.setFieldValue('alertOnExploitability', prevExploit);
                      }
                    }}
                    mb={exploitEnabled ? 12 : 0}
                  />
                  {exploitEnabled && (
                    <Slider
                      min={0.01}
                      max={1}
                      step={0.01}
                      value={form.values.alertOnExploitability}
                      onChange={(val) => {
                        const rounded = roundToTwo(val);
                        form.setFieldValue('alertOnExploitability', rounded);
                        setPrevExploit(rounded);
                      }}
                      ml="xl"
                      inverted
                    />
                  )}
                </Flex>
              </Box>
            </Card>

            {/* ================================================================
             * DELIVERY METHOD (e‑mail)
             * ============================================================== */}
            <Card p={24}>
              <Flex align="center" gap={4} mb={4}>
                <Icon icon="paper-plane" className={styles.slidersIcon} />
                <Text fw={700}>{t('page.userSettings.vulnAlerts.deliveryMethod')}</Text>
              </Flex>

              <Box ml="xl">
                <MultiSelect
                  withinPortal
                  data={emailOptions}
                  value={form.values.targetEmails}
                  onChange={(vals) => form.setFieldValue('targetEmails', vals)}
                  label={
                    <>
                      {t('page.userSettings.vulnAlerts.sendEmailTo')}
                      <span className={styles.requiredStar}>*</span>
                    </>
                  }
                  searchable
                  creatable
                  searchValue={emailSearch}
                  onSearchChange={setEmailSearch}
                  getCreateLabel={(query) => `+ ${t('global.add')} ${query}`}
                  onCreate={handleCreateEmail}
                  placeholder={t('page.userSettings.vulnAlerts.emailPlaceholder')}
                  error={form.errors.targetEmails}
                  rightSection={<span />} // hide chevron
                  rightSectionWidth={0}
                  classNames={{
                    label: styles.deliveryMethodLabel,
                    value: styles.deliveryMethodValue,
                  }}
                  /* Intercept ‘Enter’ to add the email instead of submitting form */
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (emailSearch.trim()) {
                        handleCreateEmail(emailSearch);
                        setEmailSearch('');
                      }
                    }
                  }}
                />
              </Box>
            </Card>
          </>
        )}
      </Flex>
    </div>
  );
};
